/**
 * LumaHMS brand mark and wordmark, redrawn as SVG/text from the official logo:
 * teal (#0D9488) rounded cross, amber (#F59E0B) centre dot, peach (#FBDAAD) halo;
 * wordmark "luma" in teal and "HMS" in slate (#475569), Poppins.
 */
import { cn } from '@/lib/utils'

export function Logo({ className, title }: { className?: string; title?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} role={title ? 'img' : undefined} aria-hidden={title ? undefined : true} aria-label={title}>
      <circle cx="30" cy="31" r="19" fill="#FBDAAD" opacity="0.85" />
      <rect x="22" y="3" width="20" height="58" rx="7" fill="#0D9488" />
      <rect x="3" y="22" width="58" height="20" rx="7" fill="#0D9488" />
      <circle cx="32" cy="32" r="7.5" fill="#F59E0B" />
    </svg>
  )
}

/**
 * `tone="light"` (default) for light backgrounds; `tone="dark"` for dark backgrounds
 * (e.g. the app sidebar or the website hero), where the colours are lifted for contrast.
 */
export function Wordmark({ tone = 'light', dark, size = 'md', className, hideTextOn }: { tone?: 'light' | 'dark'; dark?: boolean; size?: 'sm' | 'md' | 'lg'; className?: string; hideTextOn?: string }) {
  const onDark = dark ?? tone === 'dark'
  const s = { sm: ['h-7 w-7', 'text-[17px]'], md: ['h-8 w-8', 'text-[20px]'], lg: ['h-11 w-11', 'text-[28px]'] }[size]
  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <Logo className={cn(s[0], 'shrink-0')} />
      {/* Logotype: exact brand colours (WCAG 1.4.3 exempts logotypes from contrast minimums). */}
      <span data-logotype="" className={cn('font-display font-medium leading-none tracking-tight', s[1], hideTextOn)}>
        <span className={onDark ? 'text-[#2dd4bf]' : 'text-[#0D9488] dark:text-[#2dd4bf]'}>luma</span>
        <span className={onDark ? 'text-[#cbd5e1]' : 'text-[#475569] dark:text-[#cbd5e1]'}>HMS</span>
      </span>
    </span>
  )
}
